import { HStack, Icon, Stack, Text, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import { CheckmarkCircleIcon, CircleAlertIcon } from "@/components/icons"
import MarkdownParser from "@/components/MarkdownParser"
import { type ProsAndConsBlock as ProsAndConsBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface ProsAndConsBlockProps extends ContentBlockTitleNumberProps {
  block: ProsAndConsBlockType
}

const ProsAndConsBlock: FC<ProsAndConsBlockProps> = ({
  block,
  titleNumber,
}) => {
  const {
    darker_bg,
    list = [],
    subtitle,
    title,
    two_columns_pros_and_cons,
  } = block

  const trimmedTitle = title?.trim()
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription =
    subtitle !== undefined || subtitle !== "" || subtitle !== null
  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="pros-and-cons-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns_pros_and_cons ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns_pros_and_cons ? 6 : 8 }}
      justifyContent="flex-end"
    >
      <VStack
        alignItems="flex-start"
        flexGrow={1}
        maxWidth={{
          base: "100%",
          md: two_columns_pros_and_cons
            ? title !== undefined
              ? "30%"
              : "100%"
            : "100%",
        }}
        {...getStickyContentBlockLeftProps(two_columns_pros_and_cons)}
      >
        {(hasTitle || hasDescription) && (
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={subtitle}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        )}
      </VStack>

      {list.length > 0 && (
        <VStack
          alignItems="flex-start"
          flexBasis={{ base: "auto", md: "0" }}
          flexGrow={1}
          maxWidth={{
            base: "100%",
            md: two_columns_pros_and_cons
              ? title !== undefined
                ? "70%"
                : "100%"
              : "100%",
          }}
          pl={!hasTitle && two_columns_pros_and_cons ? { base: 0, md: 8 } : 0}
        >
          {list.map((item: { type: "pro" | "con"; value: string }) => (
            <HStack
              alignItems={"center"}
              flex="1"
              flexGrow={1}
              gap={6}
              justifyContent={"flex-start"}
              key={`${item.type}-${item.value}`}
              py={2}
              w="full"
            >
              <BlurFade
                alignItems={"center"}
                flex="1"
                flexGrow={1}
                gap={6}
                inView
                justifyContent={"flex-start"}
                py={2}
                w="full"
              >
                {item?.type === "pro" ? (
                  <VStack
                    _after={{
                      backgroundColor: "green.500/20",
                      borderRadius: "full",
                      content: '""',
                      height: "170%",
                      left: "-35%",
                      position: "absolute",
                      top: "-35%",
                      width: "170%",
                      zIndex: 1,
                    }}
                    _before={{
                      backgroundColor: "green.500/40",
                      borderRadius: "full",
                      content: '""',
                      height: "120%",
                      left: "-10%",
                      position: "absolute",
                      top: "-10%",
                      width: "120%",
                      zIndex: 2,
                    }}
                    flexShrink={0}
                    p={1}
                    position="relative"
                    rounded={"full"}
                    zIndex={2}
                  >
                    <Icon
                      _dark={{
                        color: "green.300",
                      }}
                      as={CheckmarkCircleIcon}
                      color="green.700"
                      size={"md"}
                      zIndex={3}
                    />
                  </VStack>
                ) : (
                  <VStack
                    _after={{
                      backgroundColor: "red.500/10",
                      borderRadius: "full",
                      content: '""',
                      height: "170%",
                      left: "-35%",
                      position: "absolute",
                      top: "-35%",
                      width: "170%",
                      zIndex: 1,
                    }}
                    _before={{
                      backgroundColor: "red.500/40",
                      borderRadius: "full",
                      content: '""',
                      height: "120%",
                      left: "-10%",
                      position: "absolute",
                      top: "-10%",
                      width: "120%",
                      zIndex: 2,
                    }}
                    flexShrink={0}
                    p={1}
                    position="relative"
                    rounded={"full"}
                    zIndex={2}
                  >
                    <Icon
                      _dark={{
                        color: "red.300",
                      }}
                      as={CircleAlertIcon}
                      color="red.700"
                      size={"md"}
                      zIndex={3}
                    />
                  </VStack>
                )}
                <Text
                  color="fg.muted"
                  fontFamily="code"
                  fontSize={{ base: "sm", lg: "lg" }}
                  fontWeight="bold"
                >
                  {item?.value}
                </Text>
              </BlurFade>
            </HStack>
          ))}
        </VStack>
      )}
    </Stack>
  )
}

export default ProsAndConsBlock
